import { scaleLinear, scaleTime } from 'd3-scale';
import { ClientPointEvent } from 'd3-selection';
import * as d3Shape from 'd3-shape';
import { timeMinutes } from 'd3-time';
import { DateTime } from 'luxon';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useHover from '@react-hook/hover';
import styled from 'styled-components';
import useSize from '@react-hook/size';
import { getUnits } from '@metservicedev/ww-get-units/src/ww-get-units';
import { Loading } from '@mono/metui/src/loading';

import { transformTimeRangeToDurations } from '../../utils/timeRangeMagic';
import DataDownloader from '../../components/DataDownloader';
import { IWidgetBlockComponent } from '../types/IWidgetBlock';
import Axis from './Axis';
import axisCreatePropsFactory from './axisCreatePropsFactory';
import Description from './Description';
import Dots from './Dots';
import Grid, { GridProps } from './Grid';
import Header from './Header';
import Legend from './Legend';
import MinimaAndMaxima from './MinimaAndMaxima';
import { SVGHorizontalLine, SVGHorizontalLineProps } from './SVGHorizontalLine';
import TimelineHighlights from './TimelineHighlights';
import { Datum, LineGraphModel } from './types';
import VerticalLine, { IMargins } from './VerticalLine';
import _ from 'lodash';

const DEFAULT_DATA_HIGHLIGHT_HEIGHT = 20;

const StyledLineGraphDiv = styled.div<{ fontSize: number; graphBoundaryId: string }>`
    font-size: ${({ fontSize }) => `${fontSize}%`};
    width: 100%;
    height: 100%;
    position: relative;

    .daysAxisLabel,
    .Grid-y,
    .Grid-x,
    .daysMidnightAxisLabel,
    .timesAxis {
        clip-path: url(#${({ graphBoundaryId }) => graphBoundaryId});
    }
`;

const LineGraph: React.FC<IWidgetBlockComponent<LineGraphModel>> = ({ widgetModel, isLoading }) => {
    if (!widgetModel || isLoading) {
        return <span>Loading Line Graph...</span>;
    }

    const containerDiv = useRef<HTMLDivElement>(null);
    const isHovering = useHover(containerDiv);

    const [mouseCoords, setMouseCoords] = useState<ReturnType<typeof clientPoint> | undefined>();
    const [selectedDatapoints, setSelectedDatapoints] = useState<number[]>([]);
    const [maxLabelWidthY, setMaxLabelWidthY] = useState(10);
    const [maxLabelHeightY, setMaxLabelHeightY] = useState(10);
    const [maxLabelHeightX, setMaxLabelHeightX] = useState(50);
    const [margins, setMargins] = useState<IMargins>({ top: 10, right: 0, bottom: maxLabelHeightX * 2.8, left: 20 });
    const [indexesToFilter, setIndexesToFilter] = useState<number[]>([]);
    const svgRef = useRef<SVGSVGElement>(null);

    const headerRef = useRef<HTMLDivElement>(null);
    const [headerWidth, headerHeight] = useSize(headerRef);
    const headerBounds = {
        width: headerWidth,
        height: headerHeight,
    };

    const legendRef = useRef<HTMLDivElement>(null);
    const [legendWidth, legendHeight] = useSize(legendRef);
    const legendBounds = {
        width: legendWidth,
        height: legendHeight,
    };

    const descriptionRef = useRef<HTMLDivElement>(null);
    const [descriptionWidth, descriptionHeight] = useSize(descriptionRef);
    const descriptionBounds = {
        width: descriptionWidth,
        height: descriptionHeight,
    };

    const [boundsWidth, boundsHeight] = useSize(containerDiv);
    const containerDivBounds = {
        width: boundsWidth,
        height: boundsHeight,
    };

    const [nowValue, setNowValue] = useState<number>();

    const [svgHeight, setSvgHeight] = useState(250);
    const [width, setWidth] = useState(250);
    const [height, setHeight] = useState(250);
    const fullWidth = containerDivBounds.width || 250;
    const fullHeight = (containerDivBounds.height || 250) - 4;

    const model = widgetModel;

    const {
        horizontalLines,
        useFixedOffset,
        offsetLeft,
        description,
        title,
        time,
        fontSize,
        graphData,
        timeStepsPerDay,
        timeRange,
        subTitle,
        enableXAxis,
        enableYAxis,
        enableNowLine,
        enableNowLineText,
        legendFullWidth,
        startTime,
        endTime,
        viewblockState,
        settings,
    } = model;

    const groupedData = graphData.map(({ data, dimensions }) => {
        return dimensions && dimensions.length > 0
            ? dimensions
                  ?.map((dimension) => {
                      const group = Object.values(_.groupBy(data, dimension));
                      return group;
                  })
                  .flat()
            : [data];
    }) as unknown as Datum[][][];

    const timeScale = scaleLinear()
        .domain([startTime.valueOf(), endTime.valueOf()])
        .range([margins.left, fullWidth - margins.right]);

    useLayoutEffect(() => {
        setMargins((prevMargins) => ({
            ...prevMargins,
            bottom: maxLabelHeightX * 2.8, // Line graph has two legends at the bottom. This value was empirical tested.
            left: useFixedOffset ? offsetLeft : maxLabelWidthY,
        }));
    }, [useFixedOffset, offsetLeft, maxLabelWidthY]);

    useEffect(() => {
        if (time && enableNowLine) {
            setNowValue(xScale(DateTime.fromISO(time.iso8601, { zone: time.timezone }).valueOf()));
        }
    }, [enableNowLine, margins, startTime, endTime, width]);

    const findClosestData = (data: Datum[], domainX: number): number => {
        const { index } = data.reduce<{ index: number; distance: number }>(
            (cumulated, { x }, index) => {
                const distance = Math.abs(x - domainX);

                return distance < cumulated.distance ? { distance, index } : cumulated;
            },
            { index: 0, distance: Number.MAX_SAFE_INTEGER }
        );
        return index;
    };

    //Recreates d3-selection clientPoint, with better scale handling for Firefox
    const clientPoint = (node: SVGSVGElement, { clientX, clientY }: ClientPointEvent): [number, number] => {
        const scale = node.getBoundingClientRect().width / parseFloat(node.attributes['width'].value); //Ratio between what SVG thinks its width is and actual width

        const point = new DOMPoint(clientX, clientY);
        let matrix = node.getScreenCTM() ? node.getScreenCTM() : undefined;

        if (matrix?.a === 1) matrix = matrix?.scale(scale); //To handle Firefox not respecting scale with inversing a matrix
        const { x, y } = point.matrixTransform(matrix?.inverse());

        return [x, y];
    };

    const onMouseMove = (e: React.MouseEvent<SVGGElement>) => {
        if (!model || !svgRef.current) {
            return;
        }

        const newMouseCoords = clientPoint(svgRef.current, e);

        const domainX = timeScale.invert(newMouseCoords[0]);
        const newSelectedDatapoints = groupedData.map((data) => findClosestData(data[0], domainX)); //Always return closest of 0th data, as it will either be the only value, or in ensemble, the control value.
        const isMouseWithinTimeRange = domainX - startTime.valueOf() < 0 && endTime.valueOf() - domainX > 0;
        setMouseCoords(isMouseWithinTimeRange ? undefined : newMouseCoords);
        setSelectedDatapoints(newSelectedDatapoints);
    };

    const onMouseLeave = () => {
        setMouseCoords(undefined);
        setSelectedDatapoints([]);
    };

    const timeStepsPerDayInHours = 24 / timeStepsPerDay;
    const { start, end } = transformTimeRangeToDurations(timeRange);
    const days = Math.abs(end.as('days') - Math.abs(start.as('days')));
    const dots = graphData.filter(({ pointStyle }, i) => pointStyle !== 'line' && !indexesToFilter.includes(i));
    const lines = graphData.filter((_, i) => !indexesToFilter.includes(i));
    const groupedLines = groupedData.filter((_, i) => !indexesToFilter.includes(i));

    const graphDataWithHightlightedTimeline = graphData.filter(
        ({ highlightMinima, minimaHighlightOnTimeline, highlightMaxima, maximaHighlightOnTimeline }) =>
            (highlightMinima && minimaHighlightOnTimeline) || (highlightMaxima && maximaHighlightOnTimeline)
    );

    const highlightedTimelineGroupHeight = graphDataWithHightlightedTimeline.length * DEFAULT_DATA_HIGHLIGHT_HEIGHT;

    useLayoutEffect(() => {
        const headerHeight = headerBounds.height;
        const legendHeight = legendBounds.height;
        const descriptionHeight = descriptionBounds.height;
        const headerLegendAndDescriptionHeight = headerHeight + legendHeight + descriptionHeight;
        const newSvgHeight = fullHeight - headerLegendAndDescriptionHeight;
        setSvgHeight(newSvgHeight);
    }, [headerBounds.height, legendBounds.height, descriptionBounds.height, fullHeight]);

    useLayoutEffect(() => {
        const newHeight = svgHeight - margins.top - (margins.bottom + highlightedTimelineGroupHeight);
        setHeight(newHeight);
    }, [svgHeight, margins.top, margins.bottom]);

    useLayoutEffect(() => {
        const newWidth = fullWidth - margins.left - margins.right;
        setWidth(newWidth);
    }, [fullWidth, margins.left, margins.right]);

    const yMaxData = Math.max(
        ...graphData
            .map((graph) => graph.data)
            .map((datum) => datum.flat(10).map((d) => d.y))
            .flat()
            .filter((d) => d),
        Number.MIN_SAFE_INTEGER
    );

    const yMinData = Math.min(
        ...graphData
            .map((graph) => graph.data)
            .map((datum) => datum.flat(10).map((d) => d.y))
            .flat()
            .filter((d) => d),
        Number.MAX_SAFE_INTEGER
    );
    const yMaxHorizontalLine = Math.max(...horizontalLines.map(({ y }) => y));
    const yMinHorizontalLine = Math.min(...horizontalLines.map(({ y }) => y));

    const maxYGraphValue = Math.max(yMaxData, yMaxHorizontalLine);
    const minYGraphValue = Math.min(yMinData, yMinHorizontalLine);

    const yMaxValue = isNaN(maxYGraphValue) || maxYGraphValue === Number.MIN_SAFE_INTEGER ? 5 : maxYGraphValue;
    const yMinValue = isNaN(minYGraphValue) || minYGraphValue === Number.MAX_SAFE_INTEGER ? 0 : minYGraphValue;

    const yDiff = yMaxValue - yMinValue;
    const yMax = yMaxValue + yDiff * 0.05;
    const yMin = yMinValue - yDiff * 0.05;

    const yScaleMargin = Math.abs(yMax - yMin) / 50;
    const xScale = useMemo(() => scaleTime().domain([startTime.valueOf(), endTime.valueOf()]).range([0, width]), [startTime, endTime, width]);
    const yScale = useMemo(
        () =>
            scaleLinear()
                .domain([yMin - yScaleMargin, yMax + yScaleMargin])
                .range([height, 0]),
        [yMin, yScaleMargin, yMax, yScaleMargin, height]
    );

    const graphBoundaryId = `graph-boundary-${width}-${height}`;

    const axisPropsFactory = axisCreatePropsFactory({
        timeStepsPerDay,
        startTime,
        endTime,
        fontSize,
        highlightedTimelineGroupHeight,
        height,
        xScale,
        yScale,
        ticks: days * timeStepsPerDay,
        timezone: time?.timezone,
        width,
        onMaxLabelWidthYChange: setMaxLabelWidthY,
        onMaxLabelHeightYChange: setMaxLabelHeightY,
        onMaxLabelHeightXChange: setMaxLabelHeightX,
    });

    const xTickValues = timeMinutes(startTime.startOf('day').toJSDate(), endTime.toJSDate(), timeStepsPerDayInHours * 60);

    const xGridProps: GridProps = {
        scale: xScale,
        orient: 'Bottom',
        translate: `translate(0, ${height})`,
        tickSize: -height,
        tickFormat: '',
        tickValues: xTickValues,
    };

    const yGridProps: GridProps = {
        scale: yScale,
        orient: 'Left',
        translate: `translate(0, 0)`,
        tickSize: -width,
        ticks: Math.floor(height / maxLabelHeightY),
        tickFormat: '',
    };

    const valuelines = graphData.map(({ curve }) =>
        d3Shape
            .line<Datum>()
            .defined(({ y }) => !isNaN(parseInt(String(y))))
            .x(({ x }) => xScale(x))
            .y(({ y }) => yScale(y!))
            .curve(d3Shape[curve])
    );

    const horizontalLinesScaleFc = d3Shape
        .line<Datum>()
        .defined(({ lineStyle }) => lineStyle !== 'none')
        .x(({ x }) => xScale(x))
        .y(({ y }) => yScale(y) + 0.5);

    const svgHorizontalLinesProps: SVGHorizontalLineProps[] = horizontalLines.map((line) => {
        const { y, lineStyle } = line;

        return {
            ...line,
            dPath: horizontalLinesScaleFc([
                { x: startTime.valueOf(), y, lineStyle },
                { x: endTime.valueOf(), y, lineStyle },
            ]),
            text: {
                x: xScale(endTime.valueOf()) - 10,
                y: yScale(y) - 4,
                value: y,
                anchor: 'end',
            },
        };
    });

    const databilityToUnit = getUnits({ settings: viewblockState.settings, type: 'lineGraph', databilityIds: viewblockState.databilities.map((d) => d.id) });
    return (
        <StyledLineGraphDiv className='lineGraphRender' fontSize={fontSize} graphBoundaryId={graphBoundaryId} ref={containerDiv}>
            {model.canDownload && isHovering && (
                <DataDownloader
                    datasetsWithData={model.datasets}
                    databilities={viewblockState.databilities}
                    databilityToUnit={databilityToUnit}
                    timezone={time?.timezone}
                    zipName='line-graph'
                />
            )}
            <Header title={title} subTitle={subTitle} fontSize={fontSize} ref={headerRef} />
            <Legend
                data={graphData.map((data, index) => {
                    return { ...data, data: groupedData[index] };
                })}
                fontSize={fontSize}
                margins={margins}
                indexesToFilter={indexesToFilter}
                updateIndexesToFilter={setIndexesToFilter}
                selectedDatapoints={selectedDatapoints}
                legendFullWidth={legendFullWidth}
                ref={legendRef}
            />
            <svg width={fullWidth} height={svgHeight} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave} style={{ cursor: 'crosshair' }} ref={svgRef}>
                <defs>
                    <clipPath id={graphBoundaryId}>
                        <rect y={-height} x={0} width={width} height={height * 2} />
                    </clipPath>
                </defs>

                <g transform={`translate(${margins.left}, ${margins.top})`}>
                    {settings.yAxisLabel && (
                        <g
                            textAnchor='end'
                            transform={`translate(-${(40 * fontSize) / 100},${axisPropsFactory.getValuesYAxisProps({ maxLabelHeightY }).height / 2}) rotate(-90)`}
                        >
                            <text style={{ fontSize: `${fontSize}%` }} className='AxisLabel AxisLabelY' fill={'#666'} textAnchor={'middle'}>
                                {settings.yAxisLabel}
                            </text>
                        </g>
                    )}
                    <g>
                        {enableXAxis && <Grid {...xGridProps} />}
                        {enableYAxis && <Grid {...yGridProps} />}

                        <Axis {...axisPropsFactory.getTimeXAxisProps()} />
                        <Axis {...axisPropsFactory.getDaysXAxisProps({ maxLabelHeightX })} />
                        <Axis {...axisPropsFactory.getValuesYAxisProps({ maxLabelHeightY })} />
                        <Axis {...axisPropsFactory.getDaysMidnightXAxisProps({ maxLabelHeightX })} />
                        <g>
                            {lines.map(({ dashArray, color, lineThickness, showLine, arrowSize, arrowLabelFrequency }, i) => {
                                return groupedLines[i].map((datum: Datum[], index) => {
                                    return (
                                        <g key={index + ' ' + i}>
                                            {showLine && (
                                                <path
                                                    key={index + ' ' + i}
                                                    className='line'
                                                    d={valuelines[i](datum) || undefined}
                                                    strokeDasharray={dashArray}
                                                    stroke={color}
                                                    strokeWidth={`${lineThickness}px`}
                                                />
                                            )}

                                            {datum
                                                .filter((datum: Datum) => datum.z)
                                                .filter((datum: Datum) => {
                                                    if (arrowLabelFrequency === undefined) return true;
                                                    if (DateTime.fromMillis(datum.x).setZone('utc').minute !== 0) return false;
                                                    return DateTime.fromMillis(datum.x).setZone('utc').hour % arrowLabelFrequency === 0;
                                                })
                                                .map((value, arrowId) => {
                                                    const size = arrowSize ?? 1;
                                                    const xPos = xScale(value.x);
                                                    const yPos = yScale(value.y);
                                                    return (
                                                        <g key={`${i}-${arrowId}`} transform={`rotate(${value.z}, ${xPos}, ${yPos})`}>
                                                            <polygon
                                                                points={`${xPos - size * 3} ${yPos - size * 5}, ${xPos} ${yPos - size * 11}, ${xPos + size * 3} ${yPos - size * 5}`}
                                                                stroke={color === 'black' || color === '#000000' ? '#595959' : '#000'}
                                                                strokeWidth={size * 1}
                                                                fill={color}
                                                            />
                                                            <line
                                                                x1={xPos}
                                                                x2={xPos}
                                                                y1={yPos - size * 5}
                                                                y2={yPos + size * 5}
                                                                stroke={color === 'black' || color === '#000000' ? '#595959' : '#000'}
                                                                strokeWidth={size * 3}
                                                            />
                                                            <line
                                                                x1={xPos}
                                                                x2={xPos}
                                                                y1={yPos - size * 6}
                                                                y2={yPos + size * 4}
                                                                stroke={color}
                                                                strokeWidth={size * 1}
                                                            />
                                                        </g>
                                                    );
                                                })}
                                        </g>
                                    );
                                });
                            })}
                            {dots.map((set, i) => (
                                <Dots
                                    key={i}
                                    selectedDatapoint={selectedDatapoints?.[i]}
                                    data={set}
                                    y={yScale}
                                    x={xScale}
                                    mouseCoords={mouseCoords}
                                    fontSize={fontSize}
                                />
                            ))}
                            {lines.map((set, i) => (
                                <MinimaAndMaxima key={i} lineData={set} xScale={xScale} yScale={yScale} />
                            ))}
                        </g>
                        {svgHorizontalLinesProps.map((svgHorizontalLineProps, i) => (
                            <SVGHorizontalLine key={i} {...svgHorizontalLineProps} />
                        ))}
                        {enableNowLine && nowValue && (
                            <g transform={`translate(${nowValue}, ${0 - margins.top})`}>
                                <VerticalLine svgHeight={svgHeight} margins={margins} text={{ x: 0, y: 22, value: enableNowLineText, anchor: 'start' }} />
                            </g>
                        )}
                        <TimelineHighlights
                            height={height}
                            margins={margins}
                            graphData={graphData.filter((_set, i) => !indexesToFilter.includes(i))}
                            graphDataWithHightlightedTimeline={graphDataWithHightlightedTimeline}
                            fullWidth={fullWidth}
                            scale={{ xScale, yScale }}
                        />
                    </g>
                </g>
                {mouseCoords && mouseCoords[0] > 0 && (
                    <g transform={`translate(${mouseCoords[0]}, 0)`}>
                        <VerticalLine svgHeight={svgHeight} margins={margins} />
                    </g>
                )}
            </svg>
            {description && <Description paddingLeft={margins.left} ref={descriptionRef} description={description} fontSize={fontSize} />}
        </StyledLineGraphDiv>
    );
};

export default LineGraph;
